import { toast } from "@/ui-plus/sonner";
import { placeOrderApiClient } from "@api-client/place_order";
import type { ResType } from '@api-client/typeUtils';
import { thermalPrinterFormat } from "@lib/utils/printerFormat";

import { currentPrinter, getPrinters } from "./getPrinters";
import { copies } from "./copiesNumber";
import { durableGetItem, durableHas } from "./durableStorage";
const orderDetailsApiPath = placeOrderApiClient[":storeOrderId"].get_order.$post;
type OrderType = ResType<typeof orderDetailsApiPath>["data"]['order'];

export const LOCALSTORAGE_KEYS = {
    FONT_SIZE_REM: 'FONT_SIZE_REM',
    FONT_WEIGHT: 'FONT_WEIGHT',
}

export const printOrder = (
    orderDetails: OrderType,
    locale: string | undefined = undefined
) => {
    toast.message(`Printing order...`)
    const fontSizeRem = durableHas(LOCALSTORAGE_KEYS.FONT_SIZE_REM) ? Number(durableGetItem(LOCALSTORAGE_KEYS.FONT_SIZE_REM)) : 0.8;
    const fontWeight = durableGetItem(LOCALSTORAGE_KEYS.FONT_WEIGHT) as 'normal' | 'bold' ?? 'normal';
    const html = thermalPrinterFormat(orderDetails, locale, fontSizeRem, fontWeight);
    printHTML(html, currentPrinter.value?.deviceName || '', copies.value || 1);
}

async function putHtmlInWindow(doc: Document, html: string) {
    await new Promise((r) => setTimeout(r, 200))
    const parsed = new DOMParser().parseFromString(html, 'text/html')
    doc.replaceChild(
        doc.importNode(parsed.documentElement, true),
        doc.documentElement,
    )
}

export const printHTML = async function (
    html: string,
    printer: string,
    copiesCount: number = 1,
) {
    if (typeof global !== "undefined") {
        const printers = await getPrinters();
        // check printer is available
        if (!printers.find(printerObj => printerObj.deviceName === printer)) {
            toast.error(`Printer ${printer} not found`);
            return;
        }
    }

    // TODO: check if printer is in the list of printers, maybe we should report it sender store email.

    if (typeof global === "undefined") {
        return new Promise(async (resolve) => {
            const popup = window.open('about:blank', '_blank');
            if (!popup) {
                toast.error('Popup blocked');
                resolve(false);
                return;
            }

            await putHtmlInWindow(popup.document, html);

            const run = async () => {
                try {
                    for (let i = 0; i < copiesCount; i++) {
                        popup.print()
                        await new Promise((r) => setTimeout(r, 1000))
                    }
                } finally {
                    popup.close();
                    resolve(true);
                }
            };
            run();
        });
    }

    // ~80mm wide so layout matches thermal paper (not a wide Letter preview).
    const popupWin = await new Promise<NWJS_Helpers.win>((resolve, reject) => {
        nw.Window.open(
            'about:blank',
            { show: false },
            async (win) => {
                if (!win) {
                    reject(new Error('Failed to open print window'));
                    return;
                }
                await putHtmlInWindow(win.window.document, html);
                resolve(win);
            },
        );
    });

    await new Promise((r) => setTimeout(r, 200));
    try {
        const isPDF = false;
        let pdfPath = '';
        let pdfPrinter = '';
        if (isPDF) {
            alert('PDF MODE IS ENABLED');
            const path = require('path')
            const os = require('os')
            pdfPath = path.join(os.homedir(), 'Desktop', `a-${Date.now()}.pdf`)
            pdfPrinter = 'Print to PDF (Mac Desktop)'
        }
        for (let i = 0; i < copiesCount; i++) {
            popupWin.print({
                autoprint: true,
                silent: true,
                headerFooterEnabled: false,
                landscape: false,
                shouldPrintBackgrounds: false,
                marginsType: 3,
                marginsCustom: {
                    marginTop: 0,
                    marginBottom: 0,
                    marginLeft: 0,
                    marginRight: 0,
                },
                copies: 1,
                printer: isPDF ? pdfPrinter : printer,
                pdf_path: isPDF ? pdfPath : undefined,
            })
            await new Promise((r) => setTimeout(r, 1_000))
        }
    } finally {
        popupWin.close(true);
    }
};